import debounce from 'lodash.debounce';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import InputTypeDataLossWarningModal, {
  needsInputTypeDataLossWarning,
} from 'components/Modals/InputTypeDataLossWarningModal';
import { STATUS } from 'projectConstants';
import { useAuthContext } from 'context/authContext';
import { getSharedModelUsers } from 'redux/HeaderSlice';
import { getIndicatorDetailStatus, getModelTime, setTabShimmer } from 'redux/IndicatorPageSlice';
import { UpdatePlangPageIndicatorTableData } from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import InputTypeTabGroupV1, { InputTypeTabOption } from './InputTypeTabGroupV1';

export interface BuilderModeInputTypeTabsV1Properties {
  inputType: string;
  indicatorId: number;
  refetchRequest: () => void;
}

const BuilderModeInputTypeTabsV1 = memo(
  ({ inputType, indicatorId, refetchRequest }: BuilderModeInputTypeTabsV1Properties) => {
    const dispatch = useDispatch<AppDispatch>();
    const [activeTab, setActiveTab] = useState('');
    const [isDataLossWarningOpen, setIsDataLossWarningOpen] = useState(false);
    const [pendingInputType, setPendingInputType] = useState<string | null>(null);
    const modelTime = useSelector(getModelTime);
    const timeGranularity = modelTime?.properties?.time_granularity.toLowerCase();
    const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
    const baseScenario = useSelector(getBaseScenario);
    const [isApiLoading, setApiLoading] = useState(false);
    const { user } = useAuthContext();
    const shareModelUsers = useSelector(getSharedModelUsers);

    const currentUserPermissions = shareModelUsers.find(
      (sharedUser: { user_id?: number; role?: string }) => user && sharedUser?.user_id === user?.id,
    );

    const disableChange =
      indicatorDetailStatus !== STATUS.SUCCESS ||
      isApiLoading ||
      currentUserPermissions?.role === 'view';

    const performInputTypeChange = useCallback(
      async (newType: string) => {
        if (activeTab === newType) return;
        setApiLoading(true);
        dispatch(setTabShimmer(true));
        const payload = {
          indicator_id: indicatorId,
          request_body: {
            type: newType,
            scenario_id: baseScenario?.id,
            bulk_input: true,
            format_excel_data: true,
          },
        };
        setActiveTab(newType);
        await dispatch(UpdatePlangPageIndicatorTableData(payload));
        await refetchRequest();
        setApiLoading(false);
      },
      [activeTab, indicatorId, baseScenario?.id, dispatch, refetchRequest],
    );

    const performInputTypeChangeReference = useRef(performInputTypeChange);
    performInputTypeChangeReference.current = performInputTypeChange;

    const debouncedInputTypeChange = useMemo(
      () =>
        debounce((newType: string) => {
          performInputTypeChangeReference.current(newType).catch(() => {});
        }, 1000),
      [],
    );

    useEffect(
      () => () => {
        debouncedInputTypeChange.cancel();
      },
      [debouncedInputTypeChange],
    );

    const requestInputTypeChange = (newType: string) => {
      if (activeTab === newType || disableChange) return;
      dispatch(setTabShimmer(true));
      if (needsInputTypeDataLossWarning(activeTab, newType)) {
        setPendingInputType(newType);
        setIsDataLossWarningOpen(true);
        return;
      }
      debouncedInputTypeChange(newType);
    };

    const handleConfirmDataLossChange = () => {
      if (pendingInputType != null) {
        performInputTypeChange(pendingInputType).catch(() => {});
      }
      setPendingInputType(null);
      setIsDataLossWarningOpen(false);
    };

    const handleCancelDataLossChange = () => {
      dispatch(setTabShimmer(false));
      setPendingInputType(null);
      setIsDataLossWarningOpen(false);
    };

    useEffect(() => {
      if (timeGranularity === 'y' && inputType === 'raw') {
        debouncedInputTypeChange('constant_by_year');
      } else {
        setActiveTab(inputType);
      }
    }, [inputType, timeGranularity, debouncedInputTypeChange]);

    const tabOptions = useMemo<InputTypeTabOption[]>(() => {
      const options: InputTypeTabOption[] = [
        { value: 'constant', label: 'Constant' },
        { value: 'constant_by_year', label: 'Yearly' },
      ];

      if (timeGranularity === 'm') {
        options.push({ value: 'raw', label: 'Monthly' });
      }

      options.push({ value: 'growth', label: 'Growth Rate' });
      return options;
    }, [timeGranularity]);

    return (
      <>
        <InputTypeTabGroupV1
          options={tabOptions}
          activeValue={activeTab}
          isDisabled={disableChange}
          onChange={requestInputTypeChange}
        />
        <InputTypeDataLossWarningModal
          isOpen={isDataLossWarningOpen}
          onCancel={handleCancelDataLossChange}
          onConfirm={handleConfirmDataLossChange}
        />
      </>
    );
  },
);

BuilderModeInputTypeTabsV1.displayName = 'BuilderModeInputTypeTabsV1';

export default BuilderModeInputTypeTabsV1;
